import * as React from 'react';
import { STATE } from 'app/constants';
import * as navigate from 'app/area/shared/navigation';
import { containerFactory, DefaultContainerProps } from 'app/infrastructure/redux';
import { SpinLoader, OverlayComponent, withPrompt, PromptProps } from 'app/components';
import { withOptions, OptionsProps } from 'app/components/options-handler';
import { Paper, PromptConfirmation } from 'ui';
import { CodetableStickyHeader } from 'app/area/codetable/item/sticky-header/codetable-sticky-header';
import { CodetableSelectorProps } from 'app/area/codetable/types';
import { actionCreators } from './actions';
import { CodetableAdministrationState } from '../types-store';
import { codetableAdministrationTranslation } from '../constants';
import { selectors as commonSelectors, getCodetableMetadata } from '../selectors';
import { selectors as codeItemSelectors } from 'app/area/shared/codetable/item/selectors';
import {
  codeAccessConfiguration, handleCreateCodeItem, handleLoadCodeItem,
  readOnlyConfiguration, substanceUomSetType
} from 'app/area/codetable/item/codetable-item-helpers';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { validate, ValidationData } from 'app/area/codetable/item/validation/validate';
import { getUserLanguageTranslation } from 'app/area/codetable/item/actions-helpers';
import { metroLineConfiguration } from 'app/area/codetable/item/configuration';
import { RefObject } from 'react';
import { routeConstants } from 'app/routes/route-constants';
import { CodeEditSectionType, CodeScreenValidate, InitializeCodeItemPayload } from 'app/area/codetable/item/types';
import { getCodetableId } from 'app/area/shared/codetable/breadcrumbs';
import { CodeScreenRenderer } from 'app/area/codetable/item/screen-renderer/code-screen-renderer';
import { supportedScreens } from 'app/area/codetable/item/screen-renderer/supported-screens';
import { validationToBooleanValues } from 'app/area/administration/shared/helpers';
import { getInvalidSection } from 'app/components/metro-line/helpers';
import { WithCodetableAccess, WithInvalidSection } from 'app/components/metro-line/types';
import { scrollToTop } from 'app/infrastructure/platform/routing';

export type CodetableItemComponentProps = DefaultContainerProps & OptionsProps & PromptProps &
  CodetableAdministrationState & CodetableSelectorProps & typeof actionCreators;

interface CodetableItemComponentState extends WithInvalidSection<CodeEditSectionType>, WithCodetableAccess {
  showTranslations: boolean;
}

export class CodetableItemComponent extends React.PureComponent<CodetableItemComponentProps, CodetableItemComponentState> {
  public static statePath: string = STATE.administrationCodetable;

  private detailsSectionRef: RefObject<HTMLDivElement> = React.createRef<HTMLDivElement>();
  private visibilitySectionRef: RefObject<HTMLDivElement> = React.createRef<HTMLDivElement>();
  private uomConversionSectionRef: RefObject<HTMLDivElement> = React.createRef<HTMLDivElement>();
  private translationsSectionRef: RefObject<HTMLDivElement> = React.createRef<HTMLDivElement>();
  private unitOfMeasureDetailsSectionRef: RefObject<HTMLDivElement> = React.createRef<HTMLDivElement>();
  private unitOfMeasureSetsSectionRef: RefObject<HTMLDivElement> = React.createRef<HTMLDivElement>();
  private substanceLinksSectionRef: RefObject<HTMLDivElement> = React.createRef<HTMLDivElement>();
  private typesRef: RefObject<HTMLDivElement> = React.createRef<HTMLDivElement>();
  private scalarValuesRef: RefObject<HTMLDivElement> = React.createRef<HTMLDivElement>();
  private conversionFactorsSectionRef: RefObject<HTMLDivElement> = React.createRef<HTMLDivElement>();
  private riskMatrixConfigurationRef: RefObject<HTMLDivElement> = React.createRef<HTMLDivElement>();

  public static getDerivedStateFromProps(props: CodetableItemComponentProps, state: CodetableItemComponentState) {
    const codes = props?.access?.data?.codes;
    return { codes: (codes !== state.codes) ? codes : undefined };
  }

  constructor(props: CodetableItemComponentProps) {
    super(props);

    this.state = {
      codes: props?.access?.data?.codes,
      invalidSection: undefined,
      showTranslations: undefined
    };
  }

  public componentDidMount() {
    scrollToTop();
    !this.state.codes && this.props.clearCodesAccess();
    handleCreateCodeItem(this.getLoadCodeItemPayload(this.props));

    !!this.props.codeId && this.switchTranslationsToggle(true);
  }

  public componentDidUpdate(prevProps: CodetableItemComponentProps) {
    handleLoadCodeItem(this.getLoadCodeItemPayload(this.props), this.getLoadCodeItemPayload(prevProps));

    if (prevProps?.item?.validation !== this.props?.item?.validation && this.state.invalidSection === undefined) {
      this.updateInvalidSection();
      this.updateTranslationsToggle();
    }
  }

  public componentWillUnmount() {
    this.resetForm();
  }

  private getLoadCodeItemPayload = (props: CodetableItemComponentProps): InitializeCodeItemPayload => (props
    ? { ...props, codetableId: getCodetableId(props.breadcrumbs), actionType: props?.params?.actionType, locale: props.sharedState.locale }
    : undefined
  );

  private getScreenValidation = (): CodeScreenValidate => {
    const { metadata: { data: metadata } } = this.props;

    const emptyValidation = (_: ValidationData) => ({});

    if (!supportedScreens || supportedScreens.length === 0 || !metadata || !metadata.codetable
      || !isDefinedIncludingZero(metadata.codetable.id) || !metadata.formName) {
      return emptyValidation;
    }

    const screenToRender = supportedScreens.find(screen => screen.id === metadata.codetable.id && screen.name === metadata.formName);

    return screenToRender && !!screenToRender.validate ? screenToRender.validate : emptyValidation;
  };

  private switchTranslationsToggle = (showTranslations: boolean) => this.setState({ showTranslations });

  private save = () => {
    const { saveCodetableItem, updateValidation, metadata: { data: metadata }, breadcrumbs,
      access: { data: { codes: { readonlySubsets, edit: canEdit } = {} } = {} }, options,
      item: { codeItem, isUomExcluded, uomConversionFactors, validation }, item,
      translation, sharedState: { language }, params: { actionType } } = this.props;

    const codetableId = getCodetableId(breadcrumbs);

    this.updateInvalidSection();

    updateValidation(validate({
      codetableId, codeItem, uomConversionFactors, metadata, translation, validation, actionType,
      validateScreen: this.getScreenValidation(), options, item
    }));
    
    this.updateTranslationsToggle();

    saveCodetableItem({
      codetableId,
      breadcrumbs,
      actionType,
      isUomExcluded,
      metadata, options, mainLanguage: language, validationErrorMessage: translation.validation_error,
      saveSubsetsOnly: !!readonlySubsets && readonlySubsets.length > 0 && !canEdit,
      substanceUomSetType: substanceUomSetType(codetableId),
      isHierarchy: this.isHierarchy()
    });
  };

  private cancel = () => navigate.replace(routeConstants.codetableAdministrationFor(this.props.breadcrumbs));

  private updateInvalidSection = () => {
    const { metadata: { data: metadata }, breadcrumbs, item: { codeItem, validation }, params: { actionType } } = this.props;

    const codetableId = getCodetableId(breadcrumbs);

    const codetableMetadata = getCodetableMetadata(metadata, codetableId);

    const configuration = metroLineConfiguration({
      detailsSectionRef: this.detailsSectionRef, supportedScreens, scalarValuesRef: this.scalarValuesRef,
      visibilitySectionRef: this.visibilitySectionRef, translationsSectionRef: this.translationsSectionRef,
      uomConversionSectionRef: this.uomConversionSectionRef,
      unitOfMeasureSetsSectionRef: this.unitOfMeasureSetsSectionRef, unitOfMeasureDetailsSectionRef: this.unitOfMeasureDetailsSectionRef,
      typesRef: this.typesRef, breadcrumbs, substanceLinksSectionRef: this.substanceLinksSectionRef,
      conversionFactorsSectionRef: this.conversionFactorsSectionRef,
      riskMatrixConfigurationRef: this.riskMatrixConfigurationRef,
      codeId: codeItem?.id, metadata: codetableMetadata, validationErrors: validationToBooleanValues(validation), actionType
    });

    const invalidSection = getInvalidSection(configuration);

    this.setState({ invalidSection });
  };

  private updateTranslationsToggle = () => {
    const { item: { validation } } = this.props;

    if (!!validation?.language) {
      this.switchTranslationsToggle(true);
    }
  };

  private resetForm = () => {
    const { resetCodeItem, clearValidation, clearCodesAccess } = this.props;

    clearCodesAccess();
    clearValidation();
    resetCodeItem();
  };

  private deleteCodeItem = () => {
    const { deleteCodeItem, translation, codeId, breadcrumbs } = this.props;

    deleteCodeItem({ breadcrumbs, codeId, successMessage: translation.code_item_successfully_deleted });
  };

  private duplicateCodeItem = () => {
    const { codeId, breadcrumbs } = this.props;
    navigate.replace(routeConstants.codetableAdministrationDuplicateCodeFor(breadcrumbs, codeId));
    return;
  };

  private getCodeItemDisplayedName = (): string => {
    const { codeId, item: { codeItem: { node, description: descriptions } = {} }, sharedState: { language } = {} } = this.props;

    if (!isDefinedIncludingZero(codeId)) {
      return undefined;
    }

    if (!!node && !!node.description) {
      return node.description;
    }

    const userLanguageDescription = !!descriptions && descriptions.find(description => description.value === language.value);

    if (!!userLanguageDescription && !!userLanguageDescription.description) {
      return userLanguageDescription.description;
    }

    return undefined;
  };

  private isHierarchy = () => getCodetableMetadata(this.props.metadata?.data, getCodetableId(this.props.breadcrumbs))?.features?.hierarchy;

  // eslint:disable:max-func-body-length
  public render() {

    const {
      item: {
        fetchingCodeId, fetchingCreateOptions, codeItem, savingCodetableItem, deletingCodeItem, validation } = {},
      item: { codeItem: { id: codeId, disabled: codeItemDisabled } = {} } = {},
      metadata: { data } = {},
      access: { fetchingCodes, fetchingCodetables, data: { codetables: { create: canCreate, administrationCentral } = {},
        codes: { edit: canEdit, move: canMove, readonlySubsets } = {} } = {} } = {},
      breadcrumbs, translation, sharedState: { language } = {}, params: { actionType } = {}
    } = this.props;

    if (fetchingCodetables !== undefined || fetchingCodes !== undefined || fetchingCodeId !== undefined || fetchingCreateOptions
      || !codeItem) {
      return <SpinLoader />;
    }

    const codetableId = getCodetableId(breadcrumbs);

    const codetableMetadata = getCodetableMetadata(data, codetableId);

    const overlayText = savingCodetableItem ? translation.saving_code : translation.deleting_code;

    if (!codetableMetadata) {
      return null;
    }

    const codeItemDisplayedName = this.getCodeItemDisplayedName();

    const longDescription = getUserLanguageTranslation(language.value, codetableMetadata, codeItem, 'longDescription')
      || codeItem?.node?.longDescription;

    const configuration = metroLineConfiguration({
      detailsSectionRef: this.detailsSectionRef, supportedScreens, scalarValuesRef: this.scalarValuesRef,
      visibilitySectionRef: this.visibilitySectionRef, translationsSectionRef: this.translationsSectionRef,
      uomConversionSectionRef: this.uomConversionSectionRef,
      unitOfMeasureDetailsSectionRef: this.unitOfMeasureDetailsSectionRef, substanceLinksSectionRef: this.substanceLinksSectionRef,
      unitOfMeasureSetsSectionRef: this.unitOfMeasureSetsSectionRef, typesRef: this.typesRef,
      conversionFactorsSectionRef: this.conversionFactorsSectionRef, breadcrumbs,
      riskMatrixConfigurationRef: this.riskMatrixConfigurationRef,
      codeId, metadata: codetableMetadata, validationErrors: validationToBooleanValues(validation), actionType
    });

    const scrollViewUrl = isDefinedIncludingZero(codeId)
      ? routeConstants.codetableAdministrationCodeFor(breadcrumbs, codeId, actionType)
      : routeConstants.codetableAdministrationAddCodeFor(breadcrumbs);

    const initialSection = this.props.section as CodeEditSectionType;
    const readOnlyConfig = readOnlyConfiguration({
      actionType, codeId, administrationCentral, canCreate, canMove, canEdit, readonlySubsets, codetableMetadata
    });

    const access = codeAccessConfiguration(codetableId, this.props.access.data, undefined);

    return <div className="entity-view">
      <Paper componentId="code-base-paper" className={undefined}>
        <OverlayComponent
          displayOverlay={savingCodetableItem || deletingCodeItem}
          overlayText={overlayText} />
        {this.props.shouldDisplayModal
          ? <PromptConfirmation
            title={translation.cancel_changes}
            description={translation.are_you_sure}
            continueText={translation.undo_all}
            cancelText={translation.go_back}
            onContinue={this.props.continue}
            onCancel={this.props.cancel}
          />
          : null}
        <CodetableStickyHeader
          save={this.save}
          cancel={this.cancel}
          codeItem={codeItem}

          deleteCodeItem={access.canDelete && this.deleteCodeItem}
          duplicateCodeItem={access.canDuplicate && this.duplicateCodeItem}

          name={codeItemDisplayedName}
          codetableName={codetableMetadata.label}
          codetableId={codetableId}
          codeId={this.props.codeId}
          translation={translation}
          longDescription={longDescription}
          disabled={codeItemDisabled}
          readOnlyConfig={readOnlyConfig}
          breadcrumbs={breadcrumbs}
          actionType={actionType}
        />
        <CodeScreenRenderer
          {...this.props}
          {...this.props.item}
          {...this.props.sharedState}
          invalidSection={this.state.invalidSection}
          supportedScreens={supportedScreens}
          codetableId={codetableId}
          readOnlyConfig={readOnlyConfig}
          hasBigStickyHeader={!!longDescription}
          configuration={configuration}
          scrollViewUrl={scrollViewUrl}
          initialSection={initialSection}
          metadata={codetableMetadata}
          actionType={actionType}
          hasTranslationsToggled={this.state.showTranslations}
          switchTranslationsToggle={this.switchTranslationsToggle}
        />
      </Paper>
    </div>;
  }
}

const selectors = { ...commonSelectors, ...codeItemSelectors };

const container = containerFactory(
  withOptions(withPrompt(CodetableItemComponent) as any),
  actionCreators,
  selectors,
  codetableAdministrationTranslation
);

export { container as CodetableItem };
